import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { 
  Upload, Download, CheckCircle2, AlertCircle, FileText, 
  ChevronRight, RotateCcw, Trophy, Users, Coins
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const STEPS = [
  { id: 'teams', label: 'Upload Teams', icon: Trophy, endpoint: '/teams/bulk' },
  { id: 'players', label: 'Upload Players', icon: Users, endpoint: '/players/bulk' },
  { id: 'auction', label: 'Auction Picks', icon: Coins, endpoint: '/auction/picks/bulk' },
];

const SAMPLE_CSVS = {
  teams: `name,short_name,logo_url
Team Alpha,ALPHA,https://example.com/alpha.png
Team Beta,BETA,https://example.com/beta.png`,
  players: `name,phone,sport,category,skill_level
John Doe,+919876543211,Badminton,Men's Singles,Advanced
Jane Smith,+919876543212,Table Tennis,Women's Singles,Intermediate`,
  auction: `player_phone,team_short_name,pick_number,price
+919876543211,ALPHA,1,1000
+919876543212,BETA,2,1500`,
};

const CsvUploader = ({ step, onSuccess, existingData }) => {
  const { activeOrgSlug } = useAuthStore();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const csvFile = acceptedFiles[0];
    if (!csvFile) return;

    setFile(csvFile);
    setResult(null);
    setErrors([]);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setHeaders(results.meta.fields || []);
        setPreview(results.data.slice(0, 10));
        
        // Basic validation
        const validationErrors = [];
        if (results.errors.length > 0) {
          results.errors.forEach(err => {
            validationErrors.push(`Row ${err.row}: ${err.message}`);
          });
        }
        setErrors(validationErrors);
      },
      error: (error) => {
        setErrors([`Parse error: ${error.message}`]);
      }
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    try {
      // Parse full file
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            let endpoint = `/orgs/${activeOrgSlug}${step.endpoint}`;
            
            // For auction picks, we need tournament ID
            if (step.id === 'auction') {
              // Get active tournament
              const tournamentsRes = await api.get(`/orgs/${activeOrgSlug}/tournaments`);
              const tournaments = tournamentsRes.data;
              if (tournaments && tournaments.length > 0) {
                const tid = tournaments[0].id;
                endpoint = `/orgs/${activeOrgSlug}/tournaments/${tid}/auction/picks/bulk`;
              }
            }

            const response = await api.post(endpoint, {
              rows: results.data
            });

            setResult({
              success: true,
              message: response.data.message || 'Upload successful',
              inserted: response.data.inserted || response.data.success_count || results.data.length,
              failed: response.data.failed || response.data.error_count || 0,
              errors: response.data.errors || []
            });
            
            toast.success(`${step.label} uploaded successfully!`);
            onSuccess && onSuccess(results.data);
          } catch (error) {
            const errMsg = error.response?.data?.detail || error.message;
            setResult({
              success: false,
              message: errMsg,
              errors: error.response?.data?.errors || [errMsg]
            });
            toast.error(`Upload failed: ${errMsg}`);
          }
        }
      });
    } catch (error) {
      toast.error('Failed to process file');
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSVS[step.id]], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sample_${step.id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setFile(null);
    setPreview([]);
    setHeaders([]);
    setErrors([]);
    setResult(null);
  };

  return (
    <div className="space-y-4">
      {/* Sample Download */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8a93a3]">
          Upload a CSV file with {step.id} data
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={downloadSample}
          className="text-[#f5c518] hover:text-[#f7d24a] hover:bg-[#161a26]"
        >
          <Download className="h-4 w-4 mr-2" />
          Download Sample
        </Button>
      </div>

      {/* Dropzone */}
      {!file && !result && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive 
              ? 'border-[#f5c518] bg-[rgba(245,197,24,0.05)]' 
              : 'border-[#252a37] hover:border-[#8a93a3]'
          }`}
          data-testid="wizard-upload-dropzone"
        >
          <input {...getInputProps()} />
          <Upload className="h-10 w-10 text-[#8a93a3] mx-auto mb-3" />
          <p className="text-[#f0f2f7] mb-1">
            {isDragActive ? 'Drop the file here' : 'Drag & drop a CSV file here'}
          </p>
          <p className="text-sm text-[#8a93a3]">
            or click to select a file
          </p>
        </div>
      )}

      {/* Preview */}
      {file && !result && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#f5c518]" />
              <span className="text-[#f0f2f7] font-medium">{file.name}</span>
              <Badge variant="secondary" className="bg-[#1d2230] text-[#8a93a3]">
                {preview.length} rows
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={reset}
              className="text-[#8a93a3] hover:text-[#f0f2f7]"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <Alert className="bg-[rgba(239,68,68,0.1)] border-[rgba(239,68,68,0.35)]" data-testid="wizard-validation-errors">
              <AlertCircle className="h-4 w-4 text-[#ef4444]" />
              <AlertDescription>
                <p className="font-medium text-[#f0f2f7] mb-2">Validation Errors:</p>
                <ul className="list-disc list-inside text-sm text-[#8a93a3] space-y-1">
                  {errors.slice(0, 5).map((err, i) => (
                    <li key={`err-${i}-${String(err).slice(0,20)}`}>{err}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Preview Table */}
          {preview.length > 0 && (
            <div className="border border-[#252a37] rounded-lg overflow-hidden" data-testid="wizard-csv-preview-table">
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#1d2230] border-b border-[#252a37]">
                      {headers.map((header) => (
                        <TableHead key={header} className="text-xs uppercase text-[#8a93a3]">
                          {header}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((row, i) => (
                      <TableRow key={`preview-row-${i}`} className="border-b border-[#252a37] hover:bg-[#1d2230]/50">
                        {headers.map((header) => (
                          <TableCell key={header} className="text-sm text-[#f0f2f7]">
                            {row[header] || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          )}

          {/* Upload Button */}
          <Button
            onClick={handleUpload}
            disabled={uploading || errors.length > 0}
            className="w-full h-12 bg-[#f5c518] text-[#0b0f18] font-semibold hover:bg-[#f7d24a]"
            data-testid="wizard-next-button"
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-[#0b0f18] border-t-transparent rounded-full" />
                Uploading...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload {preview.length} rows
              </span>
            )}
          </Button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-4">
          <Alert className={`${
            result.success 
              ? 'bg-[rgba(34,197,94,0.1)] border-[rgba(34,197,94,0.35)]' 
              : 'bg-[rgba(239,68,68,0.1)] border-[rgba(239,68,68,0.35)]'
          }`}>
            {result.success ? (
              <CheckCircle2 className="h-4 w-4 text-[#22c55e]" />
            ) : (
              <AlertCircle className="h-4 w-4 text-[#ef4444]" />
            )}
            <AlertDescription>
              <p className="font-medium text-[#f0f2f7] mb-1">{result.message}</p>
              {result.success && (
                <p className="text-sm text-[#8a93a3]">
                  Inserted: {result.inserted} {result.failed > 0 && `• Failed: ${result.failed}`}
                </p>
              )}
              {result.errors && result.errors.length > 0 && (
                <ul className="list-disc list-inside text-sm text-[#8a93a3] mt-2 space-y-1">
                  {result.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>{typeof err === 'string' ? err : JSON.stringify(err)}</li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
          
          <Button
            variant="outline"
            onClick={reset}
            className="w-full border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Upload Another File
          </Button>
        </div>
      )}
    </div>
  );
};

export default function TournamentSetupPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  const handleStepSuccess = (stepId) => {
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps([...completedSteps, stepId]);
    }
  };

  const progress = (completedSteps.length / STEPS.length) * 100;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-wizard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-2">
          Tournament Setup
        </h1>
        <p className="text-[#8a93a3]">
          Upload teams, players, and auction picks via CSV
        </p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Stepper */}
        <Card className="bg-[#161a26] border-[#252a37] h-fit" data-testid="wizard-stepper">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#8a93a3]">
              Progress
            </CardTitle>
            <Progress value={progress} className="h-2 bg-[#1d2230]" />
          </CardHeader>
          <CardContent className="pt-0">
            <nav className="space-y-1">
              {STEPS.map((step, index) => {
                const isCompleted = completedSteps.includes(step.id);
                const isCurrent = index === currentStep;
                const Icon = step.icon;
                
                return (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(index)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      isCurrent
                        ? 'bg-[rgba(245,197,24,0.10)] text-[#f5c518]'
                        : 'text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#1d2230]'
                    }`}
                  >
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      isCompleted
                        ? 'bg-[#22c55e] text-white'
                        : isCurrent
                        ? 'bg-[#f5c518] text-[#0b0f18]'
                        : 'bg-[#1d2230] text-[#8a93a3]'
                    }`}>
                      {isCompleted ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>
                    <span className="font-medium">{step.label}</span>
                    {isCurrent && <ChevronRight className="h-4 w-4 ml-auto" />}
                  </button>
                );
              })}
            </nav>
          </CardContent>
        </Card>

        {/* Content */}
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#f0f2f7]">
              {(() => {
                const Icon = STEPS[currentStep].icon;
                return <Icon className="h-5 w-5 text-[#f5c518]" />;
              })()}
              {STEPS[currentStep].label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CsvUploader 
              step={STEPS[currentStep]}
              onSuccess={() => handleStepSuccess(STEPS[currentStep].id)}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
